import React from 'react';
import {observer} from 'mobx-react';
import {
    TsVideoSubscriberController,
    VideoSubscriberEventType
} from '../ts-video-subscriber/TsVideoSubscriberController';
import classNames from 'classnames';
import {DashboardState} from '../../states/dashboard/_contracts/DashboardState';
import {VIDEO_MINIMIZED_MODES} from '../../../states/dashboard/main/main.helper';
import {TsVideoSubscriber} from '../ts-video-subscriber/TsVideoSubscriber';
import {TsDashboardTitle} from '../ts-dashboard-title/component';
import {MessageAlert} from '@techsee/techsee-ui-common/lib/message-alert';
import Translate from '../translate/Translate';
import GuidanceTooltip from '../../states/invite-new/invite-wizard/components/agent-guidance/components/GuidanceTooltip';
import {VideoPause} from '../video-pause/video-pause';
import {requireImage} from '../../utils/utils';
import {TsSnapshotReminder} from '../ts-snapshot-reminder';
import {TsCaptureImage} from '../ts-capture-image/ts-capture-image';
import {TsIdleLoader} from '../ts-idle-loader/ts-idle-loader';
import {VideoResolutionIndication} from '../video-resolution-indication/video-resolution-indication';
import {RecordingIndicator} from '../recording-indicator/component';
import {getRootStore} from '../../app.bootstrap';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import './ts-live-video.style.scss';

interface TsLiveVideoStretchedProps {
    controller: TsVideoSubscriberController;
    dashboardState: DashboardState;
    isClientPointerUnavailable: boolean;
    messageAlert?: {display: boolean; color: string; label: string};
    displayNewScanMode: boolean;
    agentSnapshotReminder?: {enabled: boolean};
    snapshotReminderController?: any;
    isAndroid: boolean;
    isToggleEnabledIdleModeUsability: boolean;
    locale: string;
    overlayControl?: React.ReactNode;
}

@observer
export class TsLiveVideoStretched extends TechseeBaseComponent<TsLiveVideoStretchedProps> {
    private stopPropagation = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    private handleInitCanvasClick = (event: React.MouseEvent | React.TouchEvent) => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_init_canvas_click, event);
    };

    private handlePreVideoClick = (event: React.MouseEvent) => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_pre_video_click, event);
    };

    private handleVideoClicked = (event?: React.MouseEvent) => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_video_clicked, event);
    };

    private handlePauseVideo = (event?: React.MouseEvent, value?: boolean) => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_pause_video, {event, value});
    };

    private handleDashboardTitleAction = (event?: React.MouseEvent) => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_handle_dashboard_title_action, event);
    };

    private handleToggleMute = () => {
        this.props.controller.sendEvent(VideoSubscriberEventType.on_toggle_mute, null);
    };

    renderInternal(): JSX.Element {
        const {
            controller,
            dashboardState,
            isClientPointerUnavailable,
            messageAlert,
            displayNewScanMode,
            agentSnapshotReminder,
            snapshotReminderController,
            isAndroid,
            isToggleEnabledIdleModeUsability,
            locale,
            overlayControl
        } = this.props;
        const translate = getRootStore().localizationService.translate;

        if (!dashboardState.areBothSidesConnected) {
            return <React.Fragment></React.Fragment>;
        }

        const videoState = dashboardState.videoModeState.videoState;
        const {MODES} = videoState;

        const containerClasses = classNames('ts-video-subscriber', 'toggle--stretched-video-mode', {
            'video-paused': videoState.isPaused,
            'video-focused': videoState.focus,
            'video-cursor-sharing': videoState.isCursorSharingActive,
            'video-minimized': !videoState.focus,
            'allow-action': !dashboardState.isObserve && !dashboardState.isToggleEnabled_NewLivePointerDesign,
            'allow-action-new': !dashboardState.isObserve && dashboardState.isToggleEnabled_NewLivePointerDesign,
            'toggle--rounded-minimized-video': dashboardState.isToggleEnabled_RoundedFloatingVideo,
            'toggle--square-minimized-video': dashboardState.minimizedVideoMode === VIDEO_MINIMIZED_MODES.SQUARE,
            'toggle--stretched-video': true,
            'toggle--scan-capture-design': dashboardState.isToggleEnabled_ScanCaptureDesign,
            'desktop-sharing': dashboardState.isDesktopSharing,
            'desktop-sharing-pause': dashboardState.isDesktopSharing && videoState.isPaused,
            'desktop-sharing-agent-has-control': videoState.remoteControl,
            'pointer-disabled': isClientPointerUnavailable,
            'hide-subscriber': !dashboardState.displayLiveVideo
        });

        const translations = {
            snapshotReminder: translate('MAIN.VIEW.SNAPSHOT_REMINDER'),
            desktopSharingPauseTooltip: translate('REACT.DASHBOARD.VIEW.DESKTOP_SHARING_PAUSE_TOOLTIP'),
            videoPaused: 'MAIN.VIEW.MODE_VIDEO_PAUSED',
            appSharingPausedByExpert: 'MAIN.VIEW.MODE_APP_SHARING_PAUSED_BY_EXPERT',
            screenSharingPausedByExpert: 'MAIN.VIEW.MODE_SCREEN_SHARING_PAUSED_BY_EXPERT',
            appSharingPausedByCustomer: 'MAIN.VIEW.MODE_APP_SHARING_PAUSED_BY_CUSTOMER',
            screenSharingPausedByCustomer: 'MAIN.VIEW.MODE_SCREEN_SHARING_PAUSED_BY_CUSTOMER',
            screenshot: 'MAIN.VIEW.SCREENSHOT'
        };

        return (
            <TsVideoSubscriber controller={controller} className={containerClasses}>
                <div
                    onTouchStart={this.handleInitCanvasClick}
                    onMouseDown={this.handleInitCanvasClick}
                    onClick={this.stopPropagation}
                    style={{width: '100%', height: '100%', position: 'absolute', top: 0, left: 0}}>
                    {/* Dashboard Title */}
                    {videoState.focus &&
                        !(dashboardState.returnToOneClickGuidance && dashboardState.displayLoaderReconnecting) && (
                            <TsDashboardTitle
                                dashboardState={dashboardState}
                                handleAction={this.handleDashboardTitleAction}
                                showMuteButton={dashboardState.displayAudioButton && dashboardState.useNewInterface}
                                toggleMute={this.handleToggleMute}
                            />
                        )}

                    {/* Message Alert */}
                    {messageAlert?.display && messageAlert?.color && messageAlert?.label && videoState.focus && (
                        <MessageAlert
                            className='use-common-styles message-alert-audio'
                            color={messageAlert!.color}
                            label={messageAlert!.label}
                        />
                    )}

                    {/* Video Paused Overlay */}
                    {videoState.isPaused && dashboardState.isToggleEnabled_RoundedFloatingVideo && (
                        <div
                            className={classNames('ts-video-paused', {
                                minimized: !videoState.focus
                            })}>
                            {/* First condition: videoPausedByDashboard OR !focus */}
                            {(videoState.videoPausedByDashboard || !videoState.focus) && (
                                <div className='ts-video-paused-image-wrapper'>
                                    <div
                                        className={classNames({
                                            'ts-video-paused-image': dashboardState.isLiveVideo,
                                            'ts-video-paused-screen':
                                                dashboardState.isScreenSharing || dashboardState.isApplicationSharing
                                        })}
                                    />
                                    <div className='ts-video-paused-text'>
                                        <Translate text={translations.videoPaused} />
                                        {dashboardState.isApplicationSharing && (
                                            <Translate text={translations.appSharingPausedByExpert} />
                                        )}
                                        {dashboardState.isScreenSharing && (
                                            <Translate text={translations.screenSharingPausedByExpert} />
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Second condition: NOT videoPausedByDashboard AND focus AND NOT desktop sharing */}
                            {!videoState.videoPausedByDashboard &&
                                videoState.focus &&
                                !dashboardState.isDesktopSharing && (
                                    <div className='ts-video-paused-by-customer'>
                                        {dashboardState.isLiveVideo && (
                                            <>
                                                <div className='ts-video-paused-chat' />
                                                <div className='ts-video-paused-live' />
                                            </>
                                        )}
                                    </div>
                                )}

                            {/* Third condition: NOT videoPausedByDashboard AND focus AND NOT desktop sharing */}
                            {!videoState.videoPausedByDashboard &&
                                videoState.focus &&
                                !dashboardState.isDesktopSharing && (
                                    <div className='ts-video-paused-by-screen-customer'>
                                        {(dashboardState.isApplicationSharing || dashboardState.isScreenSharing) && (
                                            <>
                                                <div className='ts-video-paused-screen' />
                                                <div className='ts-video-paused-text'>
                                                    {dashboardState.isApplicationSharing && (
                                                        <Translate text={translations.appSharingPausedByCustomer} />
                                                    )}
                                                    {dashboardState.isScreenSharing && (
                                                        <Translate text={translations.screenSharingPausedByCustomer} />
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                            {/* Fourth condition: NOT videoPausedByDashboard AND focus AND desktop sharing */}
                            {!videoState.videoPausedByDashboard &&
                                videoState.focus &&
                                dashboardState.isDesktopSharing && (
                                    <div className='ts-video-paused-by-customer ds'>
                                        <div className='ts-video-paused-ds' />
                                        <GuidanceTooltip num='1' text={translations.desktopSharingPauseTooltip} />
                                    </div>
                                )}
                        </div>
                    )}

                    {/* Video Tools Container */}
                    {videoState.focus && (
                        <div
                            className={classNames('video-tools-container', {
                                'tool-bar-hidden': !dashboardState.displayButtonsInVideo
                            })}>
                            <VideoPause
                                shouldDisplayPause={dashboardState.shouldDisplayPause}
                                pauseVideo={this.handlePauseVideo}
                                stretchedVideoMode={true}
                                roundedFloatingVideo={dashboardState.isToggleEnabled_RoundedFloatingVideo}
                                squareFloatingVideo={dashboardState.minimizedVideoMode === VIDEO_MINIMIZED_MODES.SQUARE}
                                requiredImage={requireImage}
                                videoPausedByDashboard={false}
                            />
                            {/* Video paused by customer in video application mode */}
                            {dashboardState.isVideoApplication && videoState.isPaused && videoState.focus && (
                                <div className='ts-video-paused-by-customer video-application' />
                            )}
                        </div>
                    )}

                    {/* Bottom Buttons Container */}
                    <div className='bottom-buttons-container'>
                        {dashboardState.displayButtonsInVideo && (
                            <div
                                id='ts-capture-wrapper'
                                className='magic-marker-screenshot ts-capture-wrapper'
                                onClick={this.stopPropagation}
                                onMouseDown={this.stopPropagation}>
                                {agentSnapshotReminder?.enabled && (
                                    <TsSnapshotReminder
                                        controller={snapshotReminderController}
                                        message={translations.snapshotReminder}
                                    />
                                )}
                                <TsCaptureImage
                                    onVideoClicked={this.handleVideoClicked}
                                    displayButtonsInVideo={dashboardState.displayButtonsInVideo as boolean}
                                    displayNewScanMode={displayNewScanMode}
                                />
                                <Translate text={translations.screenshot} />
                            </div>
                        )}
                    </div>

                    {/* Video Resolution Indication */}
                    {videoState.videoFormatResolution && dashboardState.displayButtonsInVideo && !isAndroid && (
                        <VideoResolutionIndication dimensionType={videoState.videoFormatResolution} />
                    )}

                    {/* Recording Indicator */}
                    {dashboardState.isRecording && <RecordingIndicator />}

                    {/* Idle Loader */}
                    <TsIdleLoader
                        isToggleEnabled_IdleModeUsability={isToggleEnabledIdleModeUsability}
                        isDesktopSharingSession={dashboardState.isDesktopSharingSession}
                        displayLoaderReconnecting={dashboardState.displayLoaderReconnecting}
                        endedTheMeeting={dashboardState.endedTheMeeting}
                        getLocale={() => locale}
                    />

                    {overlayControl && (
                        <div
                            style={{position: 'absolute', right: 8, bottom: 8, zIndex: 9999, pointerEvents: 'auto'}}
                            onClick={this.stopPropagation}
                            onMouseDown={this.stopPropagation}
                            onTouchStart={(e) => e.stopPropagation()}>
                            {overlayControl}
                        </div>
                    )}
                </div>
            </TsVideoSubscriber>
        );
    }
}
